import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ZoomIn, ZoomOut } from 'lucide-react';
import type { StrategyOverview } from '@mnc/contracts';
import { pick } from '../../i18n';
import { cn } from '../../components/ui';
import { Timeline, TimelineLegend, ZOOMS, type TimelineRow, type Zoom } from './Timeline';
import { LEVEL_TONE, useLevelLabel } from './shared';

/** The subtree as a timeline: every node below the scope as a bar (grouped by its parent), tasks under their leaf. */
export function ScheduleTab({ data, zoom, onZoom, today, showTasks: initialShowTasks = true, compact }: { data: StrategyOverview; zoom: Zoom; onZoom: (z: Zoom) => void; today: string; showTasks?: boolean; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const levelLabel = useLevelLabel(data.config);
  const [showTasks, setShowTasks] = useState(initialShowTasks);
  const [expand, setExpand] = useState<{ all: boolean; n: number } | undefined>(undefined);

  const rows = useMemo<TimelineRow[]>(() => {
    const dated = new Map(data.schedule.map((r) => [r.id, r]));
    const out: TimelineRow[] = [];
    for (const n of data.nodes) {
      if (n.id === data.node.id) continue;
      const s = dated.get(n.id);
      out.push({
        id: n.id,
        parentId: n.parent_id === data.node.id ? null : n.parent_id,
        code: n.code,
        label: pick(i18n.language, n.name_en, n.name_ar),
        badge: <span className={cn('shrink-0 rounded-full px-1.5 text-[9px] font-semibold uppercase', LEVEL_TONE[n.level])}>{levelLabel(n.level)}</span>,
        start: s?.start ?? n.start_date,
        end: s?.end ?? n.end_date,
        status: n.rollup.status,
        progress: n.rollup.progress,
        onClick: () => void navigate({ to: '/strategy/nodes/$id', params: { id: n.id } }),
      });
    }
    if (showTasks) {
      for (const r of data.schedule) {
        if (r.kind !== 'task') continue;
        out.push({ id: r.id, parentId: r.node_id === data.node.id ? null : r.node_id, code: r.code, label: pick(i18n.language, r.name_en, r.name_ar), start: r.start, end: r.end, status: r.status, progress: r.progress, onClick: () => void navigate({ to: '/tasks/$id', params: { id: r.id } }) });
      }
    }
    return out;
  }, [data, showTasks, i18n.language, levelLabel, navigate]);

  const undated = rows.filter((r) => !r.start && !r.end).length;
  const zi = ZOOMS.indexOf(zoom);

  return (
    <div className="card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-3 py-2 dark:border-neutral-800">
        <div className="flex flex-wrap items-center gap-3">
          {!compact && <h3 className="font-semibold">{t('strategy.schedule.title')}</h3>}
          <TimelineLegend />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1"><input type="checkbox" checked={showTasks} onChange={(e) => setShowTasks(e.target.checked)} />{t('strategy.schedule.showTasks')}</label>
          <button type="button" className="btn-secondary px-2 py-1" onClick={() => setExpand({ all: true, n: (expand?.n ?? 0) + 1 })}>{t('tasks.expandAll')}</button>
          <button type="button" className="btn-secondary px-2 py-1" onClick={() => setExpand({ all: false, n: (expand?.n ?? 0) + 1 })}>{t('tasks.collapseAll')}</button>
          <div className="flex items-center overflow-hidden rounded-lg border border-neutral-300 dark:border-neutral-700">
            <button type="button" className="px-2 py-1 disabled:opacity-40" disabled={zi === 0} onClick={() => onZoom(ZOOMS[zi - 1]!)} aria-label={t('strategy.schedule.zoomIn')}><ZoomIn size={14} /></button>
            <span className="px-2 font-medium uppercase">{t(`strategy.schedule.zoom.${zoom}`)}</span>
            <button type="button" className="px-2 py-1 disabled:opacity-40" disabled={zi === ZOOMS.length - 1} onClick={() => onZoom(ZOOMS[zi + 1]!)} aria-label={t('strategy.schedule.zoomOut')}><ZoomOut size={14} /></button>
          </div>
        </div>
      </div>
      <Timeline rows={rows} zoom={zoom} today={today} defaultCollapsedDepth={compact ? 0 : 1} expandSignal={expand} />
      {undated > 0 && <p className="border-t border-neutral-100 px-3 py-1.5 text-xs text-neutral-500 dark:border-neutral-800">{t('strategy.schedule.noDates', { count: undated })}</p>}
    </div>
  );
}
